import React from 'react';
import { createPortal } from 'react-dom';

export type AnnualBehaviorNoteListItem = {
  id: string;
  lessonDate: string;
  dayRowOrder: number;
  behaviorNote: string;
  recordMemberGroupId: string | null;
  periodLabelSnapshot: string;
  timetableTextSnapshot: string;
  updatedAt: string;
  linkedAnnualCellKey: string | null;
  linkedDateLabel: string;
  linkedWeekdayLabel: string;
  linkedPeriodLabel: string;
  orphanKind: 'none' | 'date' | 'overflow';
};

export type AnnualBehaviorNoteListModalProps = {
  open: boolean;
  onClose: () => void;
  items: AnnualBehaviorNoteListItem[];
  onOpenItem?: (annualCellKey: string) => void;
  title?: string;
  isLoading?: boolean;
};

function orphanLabel(kind: AnnualBehaviorNoteListItem['orphanKind']): string {
  if (kind === 'date') return '고아(날짜없음)';
  if (kind === 'overflow') return '고아(순서초과)';
  return '연결됨';
}

const AnnualBehaviorNoteListModal: React.FC<AnnualBehaviorNoteListModalProps> = ({
  open,
  onClose,
  items,
  onOpenItem,
  title = '행동특성 목록',
  isLoading = false,
}) => {
  if (!open) return null;
  return createPortal(
    <div
      className="cp-modal-backdrop"
      role="presentation"
      onMouseDown={(ev) => {
        if (ev.target === ev.currentTarget) onClose();
      }}
    >
      <div
        className="cp-modal cp-program-presentation-list-modal cp-annual-behavior-list-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-annual-behavior-list-title"
        onMouseDown={(ev) => ev.stopPropagation()}
      >
        <h3 id="cp-annual-behavior-list-title">{title}</h3>
        <div className="cp-modal-body cp-program-presentation-list-body">
          {isLoading ? (
            <p className="page-desc" role="status" aria-busy="true">
              행동특성 목록 불러오는 중…
            </p>
          ) : items.length === 0 ? (
            <p className="page-desc">기록된 행동특성이 없습니다.</p>
          ) : (
            <div className="cp-program-presentation-list-table-wrap">
              <table className="cp-program-presentation-list-table">
                <thead>
                  <tr>
                    <th>상태</th>
                    <th>기록 날짜</th>
                    <th>해당일 순서</th>
                    <th>표시 위치</th>
                    <th>내용</th>
                    <th>멤버그룹</th>
                    <th>수정시각</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => {
                    const orphan = it.orphanKind !== 'none';
                    const canOpen = !!it.linkedAnnualCellKey && !!onOpenItem;
                    return (
                      <tr key={it.id} className={orphan ? 'cp-annual-behavior-list-row-orphan' : ''}>
                        <td>
                          <span
                            className={[
                              'cp-annual-behavior-list-status',
                              orphan ? 'is-orphan' : 'is-linked',
                              it.orphanKind === 'overflow' ? 'is-overflow' : '',
                            ]
                              .filter(Boolean)
                              .join(' ')}
                          >
                            {orphanLabel(it.orphanKind)}
                          </span>
                        </td>
                        <td>{it.lessonDate}</td>
                        <td>{it.dayRowOrder + 1}</td>
                        <td>
                          {canOpen ? (
                            <button
                              type="button"
                              className="cp-btn-secondary cp-annual-behavior-list-open-btn"
                              onClick={() => onOpenItem?.(it.linkedAnnualCellKey!)}
                            >
                              {`${it.linkedDateLabel} ${it.linkedWeekdayLabel} ${it.linkedPeriodLabel}`.trim()}
                            </button>
                          ) : (
                            <span className="cp-annual-behavior-list-orphan-cell">
                              {it.linkedPeriodLabel || it.periodLabelSnapshot || '-'}
                            </span>
                          )}
                        </td>
                        <td className="cp-annual-behavior-list-note-cell">{it.behaviorNote}</td>
                        <td>{it.recordMemberGroupId ?? '-'}</td>
                        <td>{it.updatedAt || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="cp-modal-footer">
          <button type="button" className="cp-btn-secondary" onClick={onClose}>
            닫기
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AnnualBehaviorNoteListModal;
